/**
 * The attack spine: the once-per-declaration `on_attack_declared` fire, the area fan-out,
 * the single-target resolution, and the {@link resolve} entry point that dispatches between
 * them.
 */

import type {
  ApplyResolvedCommand,
  Cell,
  Combatant,
  EncounterState,
  ResolvedStep,
  ResourceDeltaStep,
  WeaponProfile,
} from "@lancer-vtt/domain";
import {
  ATTACK_ORDINAL_RESOURCE,
  applyResolved,
  isAreaRange,
  isCharacter,
  sideOf,
  terrainVisibility,
  worldFor,
} from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import type { DiceSource } from "../../dice";
import type { AttackMatch, ForcedAttackRequest, MovePath, TriggerFamily } from "../effects";
import {
  SEED_OVERLAY,
  NeedsDecisionSignal,
  combatantEffectIds,
  createResolutionLedger,
  enumerateArea,
  fireTrigger,
  footprintCells,
  gatherHandlers,
  gatherPilotHandlers,
  gatherStatusHandlers,
  gatherSystemHandlers,
  promptingChoices,
  recordedChoices,
  staticHandlers,
  interpretEffect,
  type EffectAtom,
  type ResolutionLedger,
} from "../effects";
import { resolveHeatCascade } from "../tables";
import { selfCheckContext } from "../selfCheck";
import { areaShapeOf, areaTemplate, rollSharedPool, type SharedPoolInject } from "./area";
import { archaicCantHarm, armorLeftAfterHit, effectiveTargetArmor, isPhysical } from "./damage";
import { requireCombatant, resolvePrimaryTarget } from "./targeting";
import {
  interactiveFire,
  isNeedsDecision,
  ResolveError,
  type NeedsDecision,
  type ResolveArgs,
  type ResolveArgsWithLedger,
  type ResolveResult,
} from "./types";
import { attackOverlay } from "./attackOverlay";
import { resolveVariableDamageType } from "./attackPreRoll";
import { deriveAttackFacts } from "./attackFacts";
import { damageRollReplacements, foldAttackCover, foldAttackModifiers } from "./attackModifiers";
import { firingProfile, isNoDamageMeleeAction, selectWeapon, techAttackProfile } from "./weapons";
import { consumeStatusCosts, foldWeaponFireBookkeeping, thrownLandingSteps, settleConsumeChoice, weaponFireCosts, type ConsumeChoice } from "./weaponConsume";
import { drawToHit } from "./toHit";
import { applyHitDamage, applyReliableFloorDamage, type HitDamageApplication } from "./damageApply";
import { fireHitTriggers, fireMissTriggers } from "./attackTriggerFires";
import { distanceScaledWeapon, hitReachesStructure, multiTargetCatch, reliableUnlessStructure, sparesAllies, areaOnHit } from "./weaponOwnDamage";
import { emitEvent, reactorsUsed, settleEvents, spendStepsNow, useReaction } from "../events/gameEvents";
import { createEventLoop, scratchOf, type Happened } from "../events/loop";
import { drawRoll, type AttackForced, type AttackForcedMissEvent, type AttackOutcomeEvent } from "../events/rollEvents";
import { attackDeclaredReactions } from "../declaredReactions";
import { missChanceReplacements } from "../events/rollReplacements";
import { outcomeReactions, outcomeReplacements, type ToHitReactionContext } from "../toHitReactionRegistry";
import { reactionLinked } from "../reactionLink";

/** What {@link areaAttackCatch} settles: the firing profile, its anchored template, and who it catches. */
type AreaCatch = {
  weapon: WeaponProfile;
  /** Where the template measures from and points: absent for a multi-target weapon's catch. */
  anchor: { measureFrom?: Cell; site?: Cell; toward?: Cell };
  targetIds: string[];
  warnings: string[];
  /** Heat the wielder takes for the extra targets (the Impact Lance's); 0 for an area weapon. */
  extraHeat: number;
  /** Caught allies a `sparesAllies` weapon (the Veil Rifle) does not attack, with the effect each gets instead. */
  spared?: { ids: string[]; effect: EffectAtom[] };
};

/**
 * The characters an area attack resolves against: the template's catch, minus the attacker, minus
 * any the measuring center can't see. `undefined` when the weapon has no area template or it can't
 * be anchored (the attack then resolves single-target). Shared by {@link resolveAreaAttack} and the
 * declared reaction window ({@link attackTargetIds}), so both see the same targets.
 */
function areaAttackCatch(args: Pick<ResolveArgs, "state" | "intent" | "effects">): AreaCatch | undefined {
  const { state, intent, effects } = args;
  const attacker = requireCombatant(state, intent.attacker);
  const weapon = firingProfile({ state, attacker, intent, effects });
  const area = areaShapeOf(weapon.range ?? []);
  if (area === undefined) return undefined;
  const template = areaTemplate(state, intent, area);
  if (template === undefined) return undefined;

  const enumerated = enumerateArea({
    state,
    selector: template.selector,
    actors: { self: attacker.id, attacker: attacker.id },
    ...(template.site !== undefined ? { site: template.site } : {}),
    ...(template.toward !== undefined ? { toward: template.toward } : {}),
  });
  // Attacker-exclusion is unconditional across all area shapes — parity with the overlay geometry
  // (`attackAffectedCombatants`) and `decisions/0051` D3 (Burst excludes the attacker regardless of
  // center). Line/Cone already exclude the apex; the filter keeps that invariant explicit. id-order (Q6).
  const caughtIds = enumerated.combatants.map((c) => c.id).filter((id) => id !== attacker.id);
  // A `sparesAllies` weapon (the Veil Rifle) doesn't attack the wielder's own side: those caught
  // characters leave the attack here, before LoS, and get the facet's effect instead.
  const spares = sparesAllies(attackOverlay(effects), selectWeapon(attacker, intent.weapon, undefined).id);
  const sparedIds = spares !== undefined ? caughtIds.filter((id) => !isHostileTo(state, id, attacker.id)) : [];
  const catchIds = caughtIds.filter((id) => !sparedIds.includes(id));

  const warnings: string[] = [...enumerated.warnings];

  // LoS exclusion (`los-attack-area-exclusion`, decision 1): a character the template catches
  // but the measuring center can't SEE isn't affected (RAW p.64 — Blast/Burst "cover and line of
  // sight ... calculated based on the center", not the attacker). Filtered here, at the fan-out,
  // rather than inside the S2.5 cover fold ({@link import("./attackModifiers").foldAttackCover},
  // which `resolveSingleTarget` calls) — that fold is ranged-only and would miss a melee-shaped
  // area attack; LoS exclusion is all-weapon. Reads the SAME D4 anchor the cover fold threads as
  // its `measureFrom` (`template.measureFrom` for Blast, attacker footprint otherwise) so
  // the hint and the wall can't drift (decision 2). A dropped target contributes zero steps
  // naturally below — no sentinel step, so replay/undo stays byte-stable. Seeking/Arcing are
  // exempt, mirroring `checkAttackLoS`'s initiation-gate exemption exactly (`economy.ts`).
  const isSeeking = weapon.tags?.some((t) => t.id === "tg_seeking") ?? false;
  const isArcing = weapon.tags?.some((t) => t.id === "tg_arcing") ?? false;
  let targetIds = catchIds;
  if (!isSeeking && !isArcing) {
    const world = worldFor(state);
    const originFp = template.measureFrom !== undefined ? [template.measureFrom] : footprintCells(world, attacker);
    if (originFp.length > 0) {
      const visible: string[] = [];
      for (const tid of catchIds) {
        const target = state.combatants[tid];
        const targetFp = target ? footprintCells(world, target) : [];
        if (targetFp.length === 0) {
          visible.push(tid); // unplaced/no-footprint — can't measure, fail open (mirrors `foldAttackCover`).
          continue;
        }
        const view = terrainVisibility({ space: world, terrain: state.terrain, from: originFp, to: targetFp, edges: state.edges, combatants: state.combatants });
        if (view.visible) {
          visible.push(tid);
        } else {
          warnings.push(`${target?.name ?? tid}: no line of sight from the blast center — not affected.`);
        }
      }
      targetIds = visible;
    }
  }
  return { weapon, anchor: template, targetIds, warnings, extraHeat: 0, ...(spares !== undefined && sparedIds.length > 0 ? { spared: { ids: sparedIds, effect: spares.effect } } : {}) };
}

/** Whether `id` stands on a different side from `attackerId` (the `hostile_to` predicate's test). */
function isHostileTo(state: EncounterState, id: string, attackerId: string): boolean {
  const c = state.combatants[id];
  const a = state.combatants[attackerId];
  return c !== undefined && a !== undefined && sideOf(c) !== sideOf(a);
}

/**
 * Run a `sparesAllies` weapon's effect on each spared ally (the Veil Rifle's soft cover), `self` the
 * wielder and `target` the ally, in id order.
 */
function sparedAllySteps(args: { state: EncounterState; attackerId: string; spared: NonNullable<AreaCatch["spared"]>; dice: DiceSource; effects: ResolveArgs["effects"] }): { steps: ResolvedStep[]; warnings: string[] } {
  const { state, attackerId, spared, dice, effects } = args;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  for (const id of spared.ids) {
    const r = interpretEffect(spared.effect, { state, actors: { self: attackerId, attacker: attackerId, target: id }, dice, overlay: attackOverlay(effects) });
    steps.push(...r.steps);
    warnings.push(...r.warnings);
  }
  return { steps, warnings };
}

/**
 * A **multi-target** weapon's catch ({@link multiTargetCatch}: the Sharanga Missiles' named targets,
 * the Impact Lance's line), when it attacks more than one; `undefined` otherwise, including for a
 * reaction, a forced single attack and a tech attack.
 */
function multiCatch(args: Pick<ResolveArgs, "state" | "intent" | "effects">): AreaCatch | undefined {
  const { state, intent, effects } = args;
  if (intent.reaction || intent.tech || effects?.forced?.single === true) return undefined;
  const attacker = requireCombatant(state, intent.attacker);
  const weaponId = selectWeapon(attacker, intent.weapon, undefined).id;
  const multi = multiTargetCatch({ state, intent, weaponId, overlay: attackOverlay(effects) });
  if (multi === undefined) return undefined;
  return { weapon: firingProfile({ state, attacker, intent, effects }), anchor: {}, targetIds: multi.targetIds, warnings: [], extraHeat: multi.extraHeat };
}

/**
 * Resolve an **area** attack (Blast / Burst / Line / Cone — area-weapons Phase 1+2) on the
 * grid-agnostic {@link enumerateArea} seam. The weapon's frozen range picks the template
 * ({@link areaShapeOf}); {@link areaTemplate} anchors it (a `disk` at the aimed origin for
 * Blast/Burst, a directional `linePattern`/`conePattern` from the attacker for Line/Cone). Every
 * **character** the template covers (the attacker excluded unconditionally — id-order) takes a
 * **separate to-hit** against **one shared damage pool** drawn once upfront (`decisions/0051`
 * D1/D2). Each hit target's own crit flag selects the shared base-or-crit value, applied through
 * **that target's own mitigation** (the per-target `resolveSingleTarget` sub-pipeline — never a raw
 * `hp_delta`), so `reactive-defenses` inherits AoE support for free. Blast measures LoS/cover from
 * the origin cell (D4); Burst/Line/Cone measure per-target. Overkill heat lands once on the wielder.
 * An area template catching no one resolves to zero steps (a no-op the room drops).
 */
function resolveAreaAttack(args: ResolveArgsWithLedger): ResolveResult {
  const { state, intent, input, actorId = "authority", effects } = args;
  const attacker = requireCombatant(state, intent.attacker);
  const caught = multiCatch({ state, intent, effects }) ?? areaAttackCatch({ state, intent, effects });
  // No area template (a single-target weapon slipped through) or an unanchorable one → single-target.
  if (caught === undefined) return resolveSingleTarget(args);
  const { weapon, anchor: template, targetIds } = caught;
  // combat-impact-vfx Phase 4: the anchor the frontend flies ONE shared projectile/impact to,
  // instead of one per target — the disk center for Blast/Burst, the aimed endpoint for Line/Cone.
  const areaOrigin = template.site ?? template.toward;
  const warnings: string[] = [...caught.warnings];
  const spared = caught.spared !== undefined ? sparedAllySteps({ state, attackerId: attacker.id, spared: caught.spared, dice: input.dice, effects }) : { steps: [], warnings: [] };
  warnings.push(...spared.warnings);

  if (targetIds.length === 0) {
    const command: ApplyResolvedCommand = { type: "apply_resolved", actorId, steps: spared.steps, kind: "attack" };
    return { kind: "resolved", command, steps: spared.steps, warnings, ...(areaOrigin !== undefined ? { areaOrigin } : {}) };
  }

  // Roll the shared pool ONCE, upfront — before any to-hit — matching area-effects Q7's locked
  // "roll the pool once, outside the per-target loop" shape (roll-timing decision, D1 addendum).
  // `npc-weapon-rider-damage` Phase 3, decision 8: every damage instance (primary + riders, in
  // source order) gets its OWN once-rolled pool here — `resolveSingleTarget`'s per-instance loop
  // picks each entry by its target's own crit flag, same as the Phase-1 primary-only shape.
  const instances = [weapon.damage, ...(weapon.riders ?? [])];
  const loop = (args.ledger.loop ??= createEventLoop({ dice: input.dice }));
  const sharedPools = instances.map((entry, instance) =>
    entry.count === 0
      ? rollSharedPool(entry, weapon.overkill, input.dice)
      : drawRoll({
          loop,
          dice: input.dice,
          roll: {
            kind: "roll",
            roller: attacker.id,
            purpose: { kind: "damage", instance, crit: false, shared: true },
            spec: { dice: [{ count: entry.count, sides: entry.sides }, { count: entry.count, sides: entry.sides }], accuracy: 0, ...(weapon.overkill ? { rerollOnes: true as const } : {}) },
          },
          draw: (d) => rollSharedPool(entry, weapon.overkill, d),
          facts: (pool) => (weapon.overkill ? { overkillHeat: pool.overkillHeat } : {}),
        }).result,
  );
  const sharedDamage: SharedPoolInject = sharedPools.map((p) => ({ baseDice: p.baseDice, critDice: p.critDice, bonus: p.bonus }));

  const steps: ResolvedStep[] = [...spared.steps];
  // Every caught target's damage lands on the state the earlier ones left (D5, BC-023): a
  // combatant can take damage from MORE than one sub-resolution in this single batch (caught as a
  // target AND handed a protector's split share), so each sub-resolution's steps fold into the
  // loop's scratch before the next one reads it — its Overshield, HP and structure alike.
  loop.land(state, spared.steps);
  // `decisions/0173`: every caught target's sub-resolution may spend the SAME capped clause, but a
  // frequency cap counts USES OF THE ABILITY (Core p.59, "1/round"). Each sub-resolution raises its
  // cap uses on the attack's one ledger, and `resolve()`'s settlement collapses them to one
  // `resource_delta +1` per (carrier, key), as it does for every shape (`events/gameEvents.ts`).
  //
  // Reaction uses get the OPPOSITE treatment — settled in raise order,
  // no dedupe, and the asymmetry with the cap collapse is deliberate. `decisions/0173`
  // rules on frequency caps ("one use of the ability, one write") and says nothing about reactions:
  // a reaction is spent by the REACTOR against their OWN damage instance, so each caught target
  // that Braces is a different combatant paying their own cost, and collapsing the list would
  // silently refund one of them. Brace is keyed `brace:<targetId>` and each caught target owns
  // exactly one sub-resolution, so it cannot self-duplicate here. The ONE shape that could put a
  // combatant in this list twice is a protector-gathered offer (`compileProtectorOffers`, prompt id
  // `<offerId>:<protectorId>`) whose carrier is bonded to two caught targets — and the right answer
  // there is to stop OFFERING the second one, not to accept two redirects for the price of one
  // reaction. Each use is a `reaction_use` cost on the attack's one ledger (`useReaction`), and
  // every later gate reads the reactors with a use pending there (`reactorsUsed`): every
  // sub-resolution consults ONE un-mutated `state.activation`, so the round-scoped gates can't see
  // that an earlier caught target's window already spent somebody's one reaction. Keyed on the
  // combatant, not `(combatant, reaction name)`, because the global reaction SLOT is the gate the
  // stale state is lying about: one reaction per turn, whatever it is called.
  // Forced attacks each caught target's settled-damage pass queued — collected, then run once by
  // the referee against the whole fan-out's post-state (`npc-features-fielded-roster` Phase 13.2).
  const fanOutForcedAttacks: ForcedAttackRequest[] = [];
  let fanOutMovePath: MovePath | undefined;
  for (const tid of targetIds) {
    const sub = resolveSingleTarget({
      ...args,
      intent: { ...intent, target: { kind: "unit", id: tid } },
      // `area-attack-reaction-offers` Phase 1 (D2): `effects` passes straight through, override
      // deleted. Every caught target's sub-resolution is as interactive as the single-target spine,
      // so a caught mech is OFFERED Brace instead of being declined for silently. The halt below
      // propagates; the room re-submits with the answer and the whole fan-out re-runs iterate-pure.
      effects,
      sharedDamage,
      // The `{TARGET_COUNT}` binding's source (`npc-features-fielded-roster`, the fan-out tock):
      // the settled catch set AFTER the LoS exclusion above, so an authored magnitude that scales
      // "for each target beyond the first" (Cluster Munitions, Core p.294) counts the characters
      // this attack actually resolves against — not the ones the template merely overlapped and
      // the blast center cannot see. Every sub-resolution gets the SAME number, which is what
      // makes "+X to ALL targets for each target beyond the first" come out even across the
      // fan-out instead of ramping with loop position.
      targetCount: targetIds.length,
      // Blast measures LoS/cover from the origin cell (D4); Burst/Line/Cone thread nothing → per-target.
      ...(template.measureFrom !== undefined ? { measureFrom: template.measureFrom } : {}),
      // The template center (Blast/Burst disk origin) feeds the on-hit `site` for `away_from:"site"`
      // Knockback (Phase 4). `template.site` is set for the radial shapes; Line/Cone leave it undefined.
      ...(template.site !== undefined ? { areaSite: template.site } : {}),
    });
    // The halt PROPAGATES — it used to be dropped on the floor here (`continue`), which is what
    // made a mid-fan-out offer unanswerable even when one was compiled. Returning it hands the
    // whole `resolve()` call back as `needs_decision`; nothing resolved so far is kept, because
    // the re-run replays it from `recordedDice` (`decisions/0094`'s mode 2, whole-resolution
    // re-run at an existing halt site). Prompts therefore arrive one caught target at a time, in
    // this loop's own id order (D1).
    if (sub.kind !== "resolved") return sub;
    loop.land(state, sub.command.steps);
    steps.push(...sub.command.steps);
    warnings.push(...sub.warnings);
    fanOutForcedAttacks.push(...(sub.forcedAttacks ?? []));
    if (sub.movePath !== undefined) fanOutMovePath = sub.movePath;
  }

  // Overkill's shared-pool heat (summed across EVERY instance's own pool — primary + riders,
  // decision 8) AND Heat (Self) (`tg_heat_self {N}`, `raw-fidelity-sweep` item 4) both land on the
  // **wielder** ONCE (the pools were rolled once, and the weapon fired once regardless of how many
  // targets its template caught — each per-target sub-resolution above skipped both, see the
  // `sharedDamage` guard in {@link foldWeaponFireBookkeeping}) — folded into ONE combined `resolveHeatCascade`
  // call for the same reason the single-target path combines them (two separate calls would each
  // recompute the cap-cross from the SAME pre-resolution heat and mis-cascade a combined total that
  // only crosses the cap when summed).
  const overkillHeatTotal = sharedPools.reduce((a, p) => a + p.overkillHeat, 0);
  // A multi-target weapon's per-extra-target heat (the Impact Lance) rides the same cascade.
  const areaSelfHeat = (weapon.selfHeat ?? 0) + caught.extraHeat;
  if (overkillHeatTotal + areaSelfHeat > 0) {
    const heatCascade = resolveHeatCascade({ target: attacker, amount: overkillHeatTotal + areaSelfHeat, dice: input.dice, turnOrdinal: state.activation.turnOrdinal, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, attackOverlay(effects), { loop }) });
    steps.push(...heatCascade.steps);
    warnings.push(...heatCascade.warnings);
  }

  // The weapon fired ONCE however many targets its template caught, so its Recharge discharge,
  // Loading and Limited costs are raised once here, after the loop, not per target ({@link
  // foldWeaponFireBookkeeping} skips them whenever `sharedDamage` is set). They settle at the
  // action's end with the other costs (BC-027).
  for (const cost of weaponFireCosts(attacker.id, weapon)) emitEvent(args.ledger, cost);

  const command: ApplyResolvedCommand = { type: "apply_resolved", actorId, steps, kind: "attack" };
  return { kind: "resolved", command, steps, warnings, ...(areaOrigin !== undefined ? { areaOrigin } : {}), ...(fanOutForcedAttacks.length > 0 ? { forcedAttacks: fanOutForcedAttacks } : {}), ...(fanOutMovePath !== undefined ? { movePath: fanOutMovePath } : {}) };
}

/**
 * {@link fireAttackDeclared}'s args — the resolution's own {@link ResolveArgs} plus the ONE
 * derived fact the declared fire needs and `ResolveArgs` can't carry (it is a wire-shaped
 * caller-supplied type; `areaAttack` is settled inside `resolve()`). The same extend-with-a-
 * derived-fact shape {@link SingleTargetArgs} already uses for `sharedDamage`/`areaSite`/
 * `ledger`, rather than a fourth positional parameter (`CODE_STYLE.md`'s hard-gated
 * `max-params` rule).
 *
 * `areaAttack` is OPTIONAL because {@link import("./preview").previewAttackChoice}'s probe
 * legitimately doesn't know it and doesn't need to: that probe exists to discover which
 * `choose` PROMPTS a declared-phase clause could raise, discards `steps` entirely, and
 * `areaAttack` is read by exactly one atom interpreter, where it only scales a damage
 * magnitude. Omitting it there is the honest answer, not an oversight.
 */
export type DeclaredFireArgs = ResolveArgs & {
  /**
   * `"after"`: the once-per-attack fire after every target has resolved ("after an attack",
   * Apocalypse Rail's die reset, `event-engine-kernel` 5.1). Only clauses whose `match` names
   * `phase: "after"` hear it; a clause with no phase stays on the declared and resolved fires.
   */
  phase?: "after";
  /**
   * **Will this attack fan out over more than one character?** `resolve()`'s own dispatch
   * predicate (`!intent.reaction && isAreaRange(range)`), threaded onto the declared-phase
   * fire context — see {@link import("../effects").FireContext.areaAttack}.
   */
  areaAttack?: boolean;
  /**
   * **The area template's anchor cell — "the targeted space".** {@link areaTemplate}'s `site`
   * for a Blast/Burst declaration (`blastOrigin`: the aimed cell for a `{kind:"cell"}` target,
   * the named unit's own cell for a `{kind:"unit"}` one), hoisted above this fire by `resolve()`
   * the same way `areaAttack` is. Undefined for a single-target attack and for Line/Cone (which
   * anchor on the attacker, not a point), and undefined for the `previewAttackChoice` probe.
   *
   * **Why it exists** (`npcf_earthshaker_shells_bombard`, Core p.294 — "characters within Burst 3
   * of the targeted space must succeed on a HULL save"): a declared-phase area selector needs the
   * AIMED SPACE, and before this field the only reachable spelling was `origin: "target"`, which
   * the bus resolves through `fire.primaryTarget ?? h.source`. `primaryTarget` is set only for a
   * `{kind:"unit"}` target, so a CELL-aimed shot silently re-centred the whole template on the
   * ATTACKER'S OWN CELL and swept the wrong characters — no warning, because the fallback binding
   * is a real placed combatant. Measured before the fix: a Bombard at hex(0,0,0) firing at
   * hex(10,0,-10) saved ITSELF and a bystander 2 spaces from itself, and never touched the two
   * characters standing at the impact. `origin: "site"` reads THIS field instead, which is
   * target-kind-blind, so the same clause is correct for both aim modes and degrades LOUDLY
   * (`enumerateArea`'s "could not resolve origin") when there is genuinely no anchor.
   */
  declaredSite?: Cell;
};

/**
 * Fire the declared-phase `"attack"` trigger — ONCE per declared attack, at {@link resolve}'s
 * single choke point, BEFORE dispatching to the single-target or area fan-out.
 * `resolveAreaAttack` calls `resolveSingleTarget` once per caught target, but the attack itself
 * is declared once: a Blast must not fire an authored declared-phase clause N times for N caught
 * targets, because that is a per-declaration event, not a per-to-hit one.
 *
 * **The fire context centers on ATTACKER + TARGET**, unlike a lifecycle point's carrier-only
 * framing: `attacker` is the declaring combatant, and `primaryTarget` is the named unit when the
 * intent targets one (`AttackTarget.kind === "unit"`). An area attack aimed at a bare cell has no
 * natural single target, so `primaryTarget` is omitted and the bus falls back to each handler's
 * own carrier as its `target` — the same fallback a lifecycle point relies on.
 *
 * **Gathers the ATTACKER's weapon-, pilot-, status- and system-borne handlers.** Firing a point
 * without gathering every source content could plausibly bind to would recreate the
 * inert-trigger dishonesty one layer down. Target-side handlers are deliberately NOT gathered
 * here: a defender's own reactive content rides `reactionRegistry.ts` instead.
 *
 * Returns the fired steps/warnings for the caller to prepend, or a `needs_decision`
 * short-circuit when an authored declared-phase effect raises an unanswered `choose`.
 *
 * **Does NOT touch the attack-ordinal counter** — that lives on {@link resolve}'s own
 * `ordinalIncrement` field, computed after this function and the single-target/area dispatch
 * both settle, and deliberately kept out of this function's returned steps and the resolution's
 * `command.steps`. Folding the ordinal's `resource_delta` in here directly breaks ~60 tests
 * across `packages/rules` that assert EXACT `.steps` arrays for a plain attack resolution; that
 * is measured, not hypothetical.
 */
export function fireAttackDeclared(
  args: DeclaredFireArgs,
  attacker: Combatant,
  ledger: ResolutionLedger,
): { steps: ResolvedStep[]; warnings: string[]; consumedCaps: { carrier: string; key: string }[] } | NeedsDecision {
  const { state, intent, input, effects, areaAttack, declaredSite } = args;
  const overlay = effects?.overlay ?? (effects ? SEED_OVERLAY : undefined);
  if (!overlay) return { steps: [], warnings: [], consumedCaps: [] };
  // The attack facts this fire needs come from {@link deriveAttackFacts} — the SAME single
  // derivation `resolveSingleTarget` reads (`resolve-single-target-decomposition` D5.2). This
  // function runs BEFORE `resolveSingleTarget`, which is why the six facts below used to be
  // recomputed here; that objection dissolved once the derivation became a pure function of
  // `(state, attacker, intent, effects)`, because a pure derivation gives the same answers
  // wherever it is called from. In particular `firstAttackThisTurn` reads the attacker's
  // per-turn ordinal, and state does not mutate mid-resolution, so the pre-increment read is
  // identical at both call sites.
  //
  // This ALSO closes the file's old standing hope. The previous comment here said the two
  // `actionIdForAttackLike` call sites "cannot disagree" because they passed identical inputs
  // to the same helper — true, but nothing enforced it, and the same was true of `weaponType`/
  // `mount`/`rangedAttack`/`techAttack`/`firstAttackThisTurn`. There is now exactly ONE
  // derivation of each, so agreement is a property of the code rather than a claim about it.
  // `declaredVerb` is `attackVerb` under this function's own name (`ad-hoc-gm-rulings` 2.4 —
  // the trigger-side twin of the `attackScope` the S3 modifier fold ({@link
  // import("./attackModifiers").foldAttackModifiers}) scopes on).
  const { baseWeapon, weaponType, mount, rangedAttack, techAttack, firstAttackThisTurn, attackVerb: declaredVerb, actionId: handlerId, reaction } =
    deriveAttackFacts({ state, attacker, intent, effects });
  // `system-granted-tech-attacks` D8/rot-check §2: the composite/action-aware id
  // (`attackHandlerId`) — NOT `baseWeapon.id` on its own. For a tech attack (INVADE) or a system-
  // granted tech action (Markerlight), `selectWeapon` can't resolve `intent.weapon` and
  // silently falls back to `weapons[0]`, so gathering off the resolved weapon's id used to reach
  // the attacker's first FROZEN WEAPON's `on_attack_declared` clauses — latent (nothing
  // shipped carries one), but the same mis-derivation class as the parity bug below.
  //
  // `npc-features-fielded-roster` Phase 8.1: this used to be a SECOND `attackHandlerId(intent,
  // baseWeapon.id)` call beside the `deriveAttackFacts` call above — the same "believed to
  // agree" shape the comment above criticises for the other facts. It is now
  // {@link import("./attackFacts").AttackFacts.actionId}, destructured as `handlerId`, so the
  // declared fire, the modifier folds and the resolved fires all read one derivation.
  // Order-boundary (Phase 2.2, `effects-gather-generalization`): hand-composed, NOT
  // `gatherHandlersAt` — this site's order is weapon → pilot → status → system (pilot BEFORE
  // status), which differs from `CARRIER_VECTORS`' canonical status → pilot → system.
  // Re-pointing would reorder emitted steps for a carrier with BOTH a pilot- and a status-borne
  // `on_attack_declared` clause — observable in replay/pinned-step tests — so this stays
  // hand-composed rather than normalizing the order to fit.
  // `trigger-event-families` Phase 2 A2: gather-time filtering coarsens from the old
  // `on_attack_declared` POINT to the `"attack"` FAMILY — this now also picks up this
  // weapon/carrier's hit/crit/miss-scoped clauses, which is fine: the fire below stamps
  // `phase: "declared"` on the event, and a resolved-only clause's `match` (`hit`/`crit`
  // set, or absent `phase`) fails to match an event with no `hit`/`crit` yet (`family.ts`'s
  // `evaluateMatch` strict-equality doc — the double-fire-avoidance mechanism).
  // `third-party-reactions` Phase 1: exclude `optional: true` clauses from this DECLARED-phase
  // automatic fire too — same reasoning as the two resolved-phase fires
  // ({@link import("./attackTriggerFires").fireHitTriggers} and
  // {@link import("./attackTriggerFires").fireMissTriggers}, which `resolveSingleTarget` calls at
  // its S11/S12 seams) and the precedent this mirrors, `resolveMoveTrigger`'s on_move exclusion. `hit`/`crit` are
  // unset at declare, so a clause authored with `match: { hit: true }` already can't match here —
  // this filter is the belt to that match's suspenders, for a clause that ever omits `match`.
  const handlers = [
    ...gatherHandlers({
      state,
      attackerId: attacker.id,
      weapon: { id: handlerId, ...(baseWeapon.tags ? { tags: baseWeapon.tags } : {}) },
      overlay,
      family: "attack",
    }),
    ...gatherPilotHandlers({
      carrierId: attacker.id,
      pilotEffectIds: combatantEffectIds(attacker),
      overlay,
      family: "attack",
    }),
    ...gatherStatusHandlers(scratchOf(ledger.loop, state), attacker.id, overlay).filter((h) => h.effect.on === "attack"),
    // LL0 DSL Primitives IV, P1(a): the attacker's frozen equipped-system handlers — the
    // system-carried gather vector, the same three-source parity `on_hit`/`on_crit`/`on_miss`
    // now carry (weapon/tag + pilot/frame + status was already here; systems were the gap).
    ...gatherSystemHandlers({ state, carrierId: attacker.id, overlay, family: "attack" }),
  ].filter((h) => h.effect.optional !== true && (args.phase !== "after" || (h.effect.match?.family === "attack" && h.effect.match.phase === "after")));
  if (handlers.length === 0) return { steps: [], warnings: [], consumedCaps: [] };
  const primaryTarget = intent.target.kind === "unit" ? intent.target.id : undefined;
  try {
    const fired = fireTrigger("attack", staticHandlers(handlers), {
      state,
      ...(primaryTarget !== undefined ? { primaryTarget } : {}),
      attacker: attacker.id,
      dice: input.dice,
      choices: effects?.interactive ? promptingChoices(intent.choices) : recordedChoices(intent.choices),
      overlay,
      // `area-bonus-damage-halving` Phase 1: the area-mode fact `resolve()` settled just above
      // this call, so a declared-phase `extra_damage` flagged as RAW BONUS DAMAGE would halve
      // too. Nothing shipped authors one at this phase today — the halving that matters rides
      // the on-hit fire — but the declared fire is one of the attack family's three, and
      // leaving it dark would make the rule's reach depend on which phase an author picked.
      ...(areaAttack !== undefined ? { areaAttack } : {}),
      // The attacker's own Save Target, so a declared-phase `force_save` rolls against the DC RAW
      // prints instead of the interpreter's flat 10 fallback. Byte-identical to the three fire
      // sites that already set it (`resolveActivate` and both RESOLVED attack fires); this one was
      // simply never wired, so EVERY declared-phase save in the corpus silently resolved at 10 —
      // one point too easy for a tier-1 NPC (Save Target 11) and six too easy at tier 3 (16).
      // Found by `npcf_earthshaker_shells_bombard`'s authoring pass, which pinned the wrong number
      // rather than hand-freezing a per-tier `dc` into a tier-shared entry (`decisions/0163`).
      defaultSaveDc: attacker.base.saveTarget ?? 10,
      // The area template's anchor — "the targeted space" — so a declared-phase `for_each`
      // selector (or a `forced_move away_from`) can say `"site"` and mean the AIMED cell rather
      // than whatever `primaryTarget ?? carrier` happens to bind to. See
      // {@link DeclaredFireArgs.declaredSite} for the silent mis-centring this replaces. The two
      // RESOLVED attack fires already thread their own `areaSite`; this is the declared fire's.
      ...(declaredSite !== undefined ? { site: declaredSite } : {}),
      // The `{TARGET_COUNT}` binding at the DECLARED phase, and the ONE honest answer available
      // here: an attack that is NOT fanning out resolves against exactly one character, so the
      // count is known to be `1`. An attack that IS fanning out has not had its template
      // enumerated or LoS-filtered yet (that happens in `resolveAreaAttack`, strictly after this
      // fire), so the count genuinely does not exist — omitted rather than guessed, the same
      // honest absence `AttackEvent.cover` carries at this phase. A `{TARGET_COUNT}` magnitude
      // authored on a declared-phase clause of an area attack therefore degrades to one target
      // WITH an advisory, and a `match: { targetCount: … }` on one stays dark; both are the
      // truthful outcome, and both are why the shipped fan-out-scaled content (Cluster Munitions)
      // belongs on the on-hit fire.
      ...(areaAttack === false ? { targetCount: 1 } : {}),
      // `trigger-event-families` Phase 1: the typed attack-family event payload — hit/crit/
      // rawD20/natural20 are `undefined` here (pre-roll, the outcome isn't known yet).
      // Phase 2 A1: `phase: "declared"` (new field, unread by any predicate/match yet).
      // `system-granted-tech-attacks` D8: `actionId` carries the SAME `handlerId` this
      // function gathers off, so a declared-phase clause can match `AttackMatch.actionId`.
      event: {
        family: "attack",
        phase: args.phase ?? "declared",
        ranged: rangedAttack,
        tech: techAttack,
        weaponType,
        mount,
        firstAttackThisTurn,
        actionId: handlerId,
        verb: declaredVerb,
        ...(reaction !== undefined ? { reaction } : {}),
      },
      // `LL0 DSL Primitives V`, P1: the resolution-scoped fact ledger — a `choose` atom
      // authored on an `on_attack_declared` clause (t_walking_armory:1's ammo pick) records
      // its decision here, readable by a LATER same-resolution `chose` predicate (the S3
      // pre-roll modifier fold — {@link import("./attackModifiers").foldAttackModifiers}, which
      // `resolveSingleTarget` calls with this ledger's `choices` — and the two resolved-phase fire
      // sites, {@link import("./attackTriggerFires").fireHitTriggers} /
      // {@link import("./attackTriggerFires").fireMissTriggers}).
      ledger,
      // `granted-movement-allowances` Phase 1.1: thread the recorded reaction-direction
      // answer + interactive flag onto the DECLARED-phase fire point too — mirrors the
      // on_hit/on_crit ({@link import("./attackTriggerFires").fireHitTriggers}) and on_miss
      // ({@link import("./attackTriggerFires").fireMissTriggers}) sites EXACTLY
      // (`intent.reactionToward` → `ctx.toward`, `effects?.interactive` → `ctx.interactive`).
      // Before this, a declared-
      // phase `move_self_toward_target{toward:"chosen"}` clause on the ATTACKER (this fire
      // site's gather is entirely attacker-sourced, so `self` always resolves to the
      // attacker) could never raise the interactive direction halt — it silently fell
      // through to the no-op path (`interpret.ts`'s `interpretMoveSelfTowardTarget`) even
      // with a live interactive caller and an already-recorded answer in hand. This is what
      // unblocks `t_hunter:1`'s Lunge (Phase 3) AND retroactively un-breaks
      // `t_siege_specialist:2`'s Impact self-push — see that entry's test (`talents/siege-specialist.2.test.ts`), which
      // already re-authored the clause to `toward:"chosen"` in anticipation of exactly this
      // fix and pinned the interim no-op deliberately non-interactively.
      ...(intent.reactionToward !== undefined ? { toward: intent.reactionToward as Cell } : {}),
      // Phase 18: the attack's placement cells, for an area-scoped `deploy_object` on this
      // declared fire (Earthshaker Shells' debris). Only the declared fire reads them: it runs
      // once per declaration, so a Blast's fan-out cannot place the segments twice.
      ...(intent.sites !== undefined ? { sites: intent.sites as Cell[] } : {}),
      ...interactiveFire(effects),
    });
    // LL0 DSL Primitives III, P2.5, gap-1: fold `on_attack_declared`'s own `consumedCaps` out
    // to the caller — mirrors the on_hit/on_crit/on_miss fire sites
    // ({@link import("./attackTriggerFires").fireHitTriggers} /
    // {@link import("./attackTriggerFires").fireMissTriggers}), which likewise RETURN their
    // `consumedCaps` for `resolveSingleTarget` to push into `consumedResourceCaps` so it reaches
    // `Resolved.resourceCapConsumeSteps` (the field a room driver actually persists through).
    // Before this fix, a `consumeResourceOnApply` cap authored on an `on_attack_declared`
    // clause was schema-valid but PERMANENTLY DECORATIVE: the gate-check worked within one
    // `resolve()` call (a fresh, uncapped `fireTrigger` invocation every time), but the
    // consume-write never reached the room, so a capped clause (e.g. t_hunter:1's Lunge,
    // meant to be 1/round) would re-fire on EVERY qualifying attack. See `resolve()`'s own
    // fold of `declared.consumedCaps` into `resourceCapConsumeSteps`.
    return { steps: fired.steps, warnings: fired.warnings, consumedCaps: fired.consumedCaps };
  } catch (sig) {
    if (sig instanceof NeedsDecisionSignal) {
      return {
        kind: "needs_decision",
        who: sig.who ?? attacker.id,
        promptId: sig.promptId,
        prompt: sig.prompt,
        options: sig.options,
        answerKind: sig.answerKind,
        // `granted-movement-allowances` Phase 3.3: this is the ONLY catch site a declared-phase
        // `move_self_toward_target` halt (Hunter's Lunge, and its Barrage-shot twin, addendum
        // A2) can reach — see `NeedsDecisionSignal.reachableCells`'s own doc for why it's
        // display-only and why it's absent for every other decision shape.
        ...(sig.reachableCells ? { reachableCells: sig.reachableCells } : {}),
        // An area deploy's site prompt (Earthshaker Shells) is raised here too.
        ...(sig.section ? { section: sig.section } : {}),
      };
    }
    throw sig;
  }
}

/**
 * Resolve a weapon attack. **Single-target** weapons (Range/Threat only) resolve against the
 * one combatant the intent points at — straight delegation to {@link resolveSingleTarget}.
 * **Area** weapons (Blast/Burst/Line/Cone, read off the frozen `WeaponProfile.range`) fan out
 * through {@link resolveAreaAttack} on the grid-agnostic `enumerateArea` seam: the weapon's
 * template (a `disk` at the aimed origin, or a directional line/cone from the attacker) is
 * enumerated once, and the attack resolves against **each character it catches** — a separate
 * to-hit per target against **one shared, rolled-once damage pool** — their steps concatenated
 * into a single `apply_resolved`, on hex **and** square alike.
 *
 *   - **Shared rolled-once pool**: the damage dice are drawn once upfront (base + crit), and
 *     each target takes whichever value its own crit flag selects through its own mitigation —
 *     never a per-target re-roll, never a raw `hp_delta`.
 *   - **Interactive fan-out**: a sub-resolution's halt propagates; the re-run replays the shared
 *     pool and every earlier target from `recordedDice`. Each caught target is resolved with the
 *     caller's own `effects`, so an unanswered `choose` (a caught mech's Brace, a protector's
 *     Intercede) raises a real prompt instead of taking its deterministic default. Prompts arrive
 *     one target at a time, in the fan-out's id order. Recorded `intent.choices` still apply.
 *   - **Reactions never fan out** — Overwatch is a single-target reaction by rule (and the room
 *     already gates it on a unit target); it delegates straight to the single-target spine.
 *   - An area template that catches **no one** resolves to zero steps — a no-op the room drops,
 *     the same as a gated-out activation.
 */
/**
 * Run one resolution stage, turning a {@link NeedsDecisionSignal} that escaped every inner catch
 * site into `needs_decision`. The inner sites (each trigger fire, the attack-outcome reactions, the
 * damage windows) catch their own halts with richer context; what reaches here is a halt from
 * outside them, which today means a `gm_ruling` in a modifier's `condition` asking the GM during
 * the pre-roll fold (`decisions/0183`).
 */
function catchDecision(run: () => ResolveResult, actorId: string): ResolveResult {
  try {
    return run();
  } catch (sig) {
    if (!(sig instanceof NeedsDecisionSignal)) throw sig;
    return { kind: "needs_decision", who: sig.who ?? actorId, promptId: sig.promptId, prompt: sig.prompt, options: sig.options, answerKind: sig.answerKind };
  }
}

/**
 * Append a weapon's `areaOnHit` effect (the Magnetic Cannon's "On hit: all characters within the
 * affected area must …") to a resolved attack: once it hit anyone, the effect runs once on every
 * character it attacked ({@link attackTargetIds}, so a missed character in the Line too, and the one
 * target of an Overwatch shot), `self` the wielder and `target` that character. Anything else, or a
 * resolution that isn't `resolved`, passes through unchanged.
 */
function withAreaOnHit(args: ResolveArgsWithLedger, result: ResolveResult): ResolveResult {
  const { state, intent, input, effects } = args;
  if (result.kind !== "resolved" || intent.tech) return result;
  const attacker = requireCombatant(state, intent.attacker);
  const overlay = attackOverlay(effects);
  const onAreaHit = areaOnHit(overlay, selectWeapon(attacker, intent.weapon, undefined).id);
  if (onAreaHit === undefined || !result.steps.some((st) => st.op === "to_hit" && st.attacker === attacker.id && st.result !== "miss")) return result;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  for (const tid of attackTargetIds(args)) {
    const r = interpretEffect(onAreaHit.effect, {
      state,
      actors: { self: attacker.id, attacker: attacker.id, target: tid },
      dice: input.dice,
      overlay,
      defaultSaveDc: attacker.base.saveTarget ?? 10,
      choices: effects?.interactive ? promptingChoices(intent.choices) : recordedChoices(intent.choices),
      ...interactiveFire(effects),
    });
    steps.push(...r.steps);
    warnings.push(...r.warnings);
  }
  return { ...result, steps: [...result.steps, ...steps], warnings: [...result.warnings, ...warnings], command: { ...result.command, steps: [...result.command.steps, ...steps] } };
}

/**
 * The characters an attack will resolve against, read the way {@link resolve} dispatches: an area
 * attack's catch ({@link areaAttackCatch}), else the one primary target. Throws the same
 * `ResolveError` `resolve` would for an unknown attacker, weapon or target. Read by the declared
 * reaction window (`declaredReactions.ts`), which runs before the attack.
 */
export function attackTargetIds(args: Pick<ResolveArgs, "state" | "intent" | "effects">): string[] {
  const { state, intent, effects } = args;
  const attacker = requireCombatant(state, intent.attacker);
  const range = (intent.tech ? techAttackProfile(intent, attacker) : selectWeapon(attacker, intent.weapon, intent.profile)).range ?? [];
  const multi = multiCatch({ state, intent, effects });
  if (multi !== undefined) return multi.targetIds;
  if (!intent.reaction && effects?.forced?.single !== true && isAreaRange(range)) {
    const caught = areaAttackCatch({ state, intent, effects });
    if (caught !== undefined) return caught.targetIds;
  }
  return [resolvePrimaryTarget(state, intent.target).id];
}

/**
 * An area or multi-target attack's catch, read the way {@link resolve} dispatches it: the targets
 * it resolves against and the Blast's measuring center. `undefined` for a single-target attack, a
 * reaction, a forced single attack, or a template that can't be anchored. Read by the forecast.
 */
export function areaCatchOf(args: Pick<ResolveArgs, "state" | "intent" | "effects">): { targetIds: string[]; measureFrom?: Cell } | undefined {
  const { state, intent, effects } = args;
  const attacker = requireCombatant(state, intent.attacker);
  const range = (intent.tech ? techAttackProfile(intent, attacker) : selectWeapon(attacker, intent.weapon, intent.profile)).range ?? [];
  const caught = multiCatch(args) ?? (!intent.reaction && effects?.forced?.single !== true && isAreaRange(range) ? areaAttackCatch(args) : undefined);
  if (caught === undefined) return undefined;
  return { targetIds: caught.targetIds, ...(caught.anchor.measureFrom !== undefined ? { measureFrom: caught.anchor.measureFrom } : {}) };
}

export function resolve(args: ResolveArgs): ResolveResult {
  const { intent } = args;
  const attacker = requireCombatant(args.state, intent.attacker);
  // `LL0 DSL Primitives V`, P1: ONE resolution-scoped fact ledger for this whole `resolve()`
  // call — created here, threaded (by reference — see `ResolutionLedger`'s own doc) into
  // `fireAttackDeclared` AND the single-target/area dispatch below, so a `choose` atom's
  // decision recorded at `on_attack_declared` is readable by a `chose` predicate on a LATER
  // same-resolution `modifier`/`triggered` clause (the pre-roll damage-type fold, an on-hit
  // rider). `resolveAreaAttack`'s per-target sub-resolutions inherit it via its own `args`
  // spread, so a Blast's declared choice still correlates across every caught target.
  const ledger = createResolutionLedger("settles");
  // The intent's event loop rides the ledger to every fire and cost this resolution raises; a
  // resolution run without one (a test, a probe's caller) gets its own over its dice.
  ledger.loop = args.effects?.loop ?? createEventLoop({ dice: args.input.dice });
  ledger.opened = ledger.loop.log.length;
  // `area-bonus-damage-halving` Phase 1: the dispatch predicate is HOISTED above the declared
  // fire so `fireAttackDeclared` can be told whether this attack is going to fan out. Pure and
  // total for a legal weapon ref (`selectWeapon` throws only on an unknown named id, before any
  // dice) — so moving it earlier reorders nothing observable; the SAME `range` then drives the
  // dispatch below, so the declared fire and the dispatch cannot disagree by construction. A tech
  // attack fires no weapon ({@link techAttackProfile}: rangeless), so it never fans out.
  const range = (intent.tech ? techAttackProfile(intent, attacker) : selectWeapon(attacker, intent.weapon, intent.profile)).range ?? [];
  // A multi-target weapon (`multiCatch`) fans out through the same area spine.
  const multi = multiCatch(args) !== undefined;
  const areaAttack = multi || (!intent.reaction && args.effects?.forced?.single !== true && isAreaRange(range));
  // NOTE the deliberate asymmetry with `resolveSingleTarget`'s own `areaAttack`
  // (`sharedDamage !== undefined`): at DECLARE time the fan-out hasn't happened, so an
  // unanchorable template that will degrade to the single-target spine still reads `true` here.
  // That is the only area fact that exists this early, and no shipped bonus-damage atom is
  // authored at the declared phase — the halving that matters rides the on-hit fire.
  // …and the template's ANCHOR CELL, hoisted for the same reason and by the same rule: the
  // declared fire is allowed to know the facts `resolve()` has already settled. `areaShapeOf` +
  // `areaTemplate` are the SAME pure pair `resolveAreaAttack` re-runs below off the same
  // `(state, intent, range)`, so the two cannot disagree; both are total (they answer `undefined`
  // for a single-target weapon, an unanchorable template, or a Line/Cone, and the field is then
  // omitted). See {@link DeclaredFireArgs.declaredSite}.
  const declaredArea = areaAttack ? areaShapeOf(range) : undefined;
  const declaredSite = declaredArea !== undefined ? areaTemplate(args.state, intent, declaredArea)?.site : undefined;
  const declared = fireAttackDeclared({ ...args, areaAttack, ...(declaredSite !== undefined ? { declaredSite } : {}) }, attacker, ledger);
  if ("kind" in declared) return declared; // needs_decision — short-circuit, same as on_hit's.
  for (const { carrier, key } of declared.consumedCaps) emitEvent(ledger, { kind: "resource_cap_use", carrier, key });
  // The reactions to the declaration (`declaredReactions.ts`, `event-engine-to-hit` Phase 4): after
  // the attacker's own declared clauses (F2a, the declarer is the subject), by initiative, on the
  // state they left. A forced attack opens none (it never did). Their steps fold into the state the
  // attack resolves against, their reactors count as spent for its later gates, and what they did to
  // the attack (a forced miss, Resistance) lands in the loop for its attempt and outcome to read.
  const overlay = attackOverlay(args.effects);
  const reacted =
    overlay === undefined || args.effects?.forced !== undefined
      ? undefined
      : attackDeclaredReactions({ state: ledger.loop.scratch(args.state), intent, targetIds: attackTargetIds(args), overlay, dice: args.input.dice, interactive: args.effects?.interactive === true, loop: ledger.loop });
  if (reacted?.kind === "needs_decision") return reacted;
  useReaction(ledger, reacted?.reactionSpentBy);
  const argsWithLedger = {
    ...args,
    ...(reacted !== undefined && reacted.steps.length > 0 ? { state: applyResolved(args.state, reacted.steps) } : {}),
    ledger,
  };
  const single = !multi && (intent.reaction || args.effects?.forced?.single === true || !isAreaRange(range));
  const sub = catchDecision(() => withAreaOnHit(argsWithLedger, single ? resolveSingleTarget(argsWithLedger) : resolveAreaAttack(argsWithLedger)), attacker.id);
  if (sub.kind !== "resolved") return sub;
  // The record leads with the declared clauses' steps, then the reactions to them, then the attack.
  const lead = [...declared.steps, ...(reacted?.steps ?? [])];
  const leadWarnings = [...declared.warnings, ...(reacted?.warnings ?? [])];
  const merged =
    lead.length === 0 && leadWarnings.length === 0
      ? sub
      : {
          ...sub,
          steps: [...lead, ...sub.steps],
          warnings: [...leadWarnings, ...sub.warnings],
          command: { ...sub.command, steps: [...lead, ...sub.command.steps] },
        };
  // After the attack (RAW "after an attack with ...", Core p.228): one fire for the whole attack,
  // reading the scratch with every target's resolution folded in.
  ledger.loop?.land(args.state, merged.steps);
  const after = fireAttackDeclared({ ...args, areaAttack, phase: "after" }, attacker, ledger);
  if ("kind" in after) return after;
  for (const { carrier, key } of after.consumedCaps) emitEvent(ledger, { kind: "resource_cap_use", carrier, key });
  const attacked =
    after.steps.length === 0 && after.warnings.length === 0
      ? merged
      : { ...merged, steps: [...merged.steps, ...after.steps], warnings: [...merged.warnings, ...after.warnings], command: { ...merged.command, steps: [...merged.command.steps, ...after.steps] } };
  // `ll0-dsl-primitives-2` Phase D (`decisions/0085`) — the attack-ordinal counter's increment
  // half, set on every COMMITTED resolution `resolve` itself produces (mirrors
  // `fireAttackDeclared`'s own once-per-declared-attack choke point — a Blast fan-out still
  // declares/increments once). See `Resolved.ordinalIncrement`'s own doc for why this is a
  // SEPARATE field, not folded into `merged.steps`/`merged.command.steps`.
  const ordinalIncrement: ResourceDeltaStep = {
    op: "resource_delta",
    target: attacker.id,
    key: ATTACK_ORDINAL_RESOURCE,
    delta: 1,
  };
  // LL0 DSL Primitives III, P2.5, gap-1: `fireAttackDeclared`'s own `consumedCaps` (the
  // `on_attack_declared` fire, computed once at the top of this function) feed the SAME
  // `resourceCapConsumeSteps` the on_hit/on_crit/on_miss fires do. Without this, a
  // `consumeResourceOnApply` cap authored on an `on_attack_declared` clause gated but never
  // actually spent (see `fireAttackDeclared`'s own doc on the gap this closes).
  //
  // Every cap use is an event on the ledger (`decisions/0211`), and the whole ledger settles here,
  // once, after every die is drawn, in raise order with one use per (carrier, key). Charge spends
  // (and the record of any a replacement waived) end the step list.
  const settled = settleEvents(ledger, { state: args.state, overlay: attackOverlay(args.effects) });
  const resourceCapConsumeSteps = settled.capSteps;
  const withSpends =
    settled.steps.length === 0
      ? attacked
      : { ...attacked, steps: [...attacked.steps, ...settled.steps], command: { ...attacked.command, steps: [...attacked.command.steps, ...settled.steps] } };
  return {
    ...withSpends,
    ordinalIncrement,
    ...(resourceCapConsumeSteps.length > 0 ? { resourceCapConsumeSteps } : {}),
    // The reactions this action used, in raise order: one sibling `spend_reaction` each (D8).
    ...(settled.reactions !== undefined ? { reactionSpentBy: settled.reactions } : {}),
    // `granted-movement-allowances` D3, site D: surface whichever direction decision this
    // resolution's ledger recorded as consumed (`fireAttackDeclared`'s declared-phase fire OR
    // `resolveSingleTarget`/`resolveAreaAttack`'s on-hit/on-crit/on-miss fires — the SAME shared
    // `ledger` threaded through all of them). See `Resolved.answeredDirectionPromptId`'s own doc.
    ...(ledger.answeredDirectionPromptId !== undefined ? { answeredDirectionPromptId: ledger.answeredDirectionPromptId } : {}),
  };
}

/**
 * Resolve an attack against the **one primary target** the intent points at (a named unit,
 * or the occupant of a targeted cell) — the single-target spine, unchanged from the A1→A6
 * build. {@link resolve} wraps this: a single-target weapon delegates straight here; an
 * area weapon (Blast/Line/Cone/Burst) calls it once per combatant the template catches,
 * synthesizing a unit-target intent for each (RAW "one attack per affected target").
 */
type SingleTargetArgs = ResolveArgsWithLedger & {
  /**
   * The area shared damage pool (area-weapons Phase 1; extended to riders by
   * `npc-weapon-rider-damage` Phase 3 decision 8) — one entry per damage instance, index-aligned
   * with `[weapon.damage, ...(weapon.riders ?? [])]`. When present, this target's crit flag selects
   * each instance's base or crit value instead of rolling its own weapon dice. Set only by the
   * Blast fan-out.
   */
  sharedDamage?: SharedPoolInject;
  /** Blast LoS/cover measure-from origin cell (D4). Blast only. */
  measureFrom?: Cell;
  /**
   * The **area template center** cell (area-weapons Phase 4) — threaded into the on-hit trigger context
   * as `site`, so a Blast/Burst-Knockback rider's `forced_move away_from: "site"` pushes each caught
   * target radially away from the blast center. Set by the Blast/Burst fan-out; `undefined` otherwise.
   */
  areaSite?: Cell;
  /**
   * **How many characters this attack fans out over** — the `{TARGET_COUNT}` binding's one
   * source. Set by the area fan-out to its OWN settled `targetIds.length`: the count AFTER the
   * per-target LoS exclusion above, so it counts the characters that actually resolve, not the
   * raw template catch. Absent on the single-target spine, where this function reads it as `1`.
   *
   * Deliberately threaded down rather than re-derived per sub-resolution: each sub-resolution is
   * handed a single-unit intent and can see only its own target, so the fan-out is the ONLY
   * place the number exists. It reaches the attacker-side modifier fold, the roll replacements'
   * facts, and both resolved-phase trigger fires.
   */
  targetCount?: number;
};

/**
 * The {@link TriggerFamily}s that actually reach `fireTrigger` in production. Every other
 * trigger point an effect can be authored against is served by a built-in registry, never the
 * content trigger bus — so a `triggered` effect bound to one of those loads, validates, and
 * silently never runs. That dishonesty is what this constant exists to keep visible:
 * `inertTriggers.ts` keys a clause's `on` against this set to report the dead ones.
 *
 * **A family joins this list in the SAME change that wires its fire, never speculatively**, and
 * `firedTriggerPoints.pin.test.ts` enforces that by grepping the real `fireTrigger(` call sites:
 * one added, removed, or retargeted without this constant moving fails the pin. The call sites
 * are the three `"attack"` fires here (declared-phase, resolved hit/crit, miss), `"status"` /
 * `"action"` / `"move"` / `"trample"` (`resolveTrampleTrigger`, `npc-features-fielded-roster`
 * Phase 5.1 — fired once per crossed character by `referee/moveFollowups.ts`'s `trampleInto`) /
 * `"damage"` (`resolveDamageTrigger`, `decisions/0175` — fired for NON-attack damage by
 * `referee/refereeIntent.ts`'s `fireDamageTakenInto`) and the computed turn/round fire in
 * `triggers.ts`, and `"save"` in `effects/interpret.ts`.
 *
 * **A family can be consumed by two mechanisms, and "off this list" does NOT mean "inert."**
 * `reactionRegistry.ts`'s `REGISTRY_CONSUMED_TRIGGER_POINTS` names the families an interception
 * window consumes outside the bus; `inertTriggers.ts` unions the two sets rather than treating
 * this one as the whole truth. `"attack"` and `"damage"` sit in BOTH: attack damage runs
 * `on:"damage"` clauses through the reaction interception window (before mitigation, so it sees
 * an immunity-nullified hit), while burn and other non-attack damage fire them here, on the bus.
 * The two site sets are disjoint, so no clause fires twice for one damage instance.
 */
export const FIRED_TRIGGER_POINTS: readonly TriggerFamily[] = [
  "attack",
  "status",
  "start_turn",
  "end_turn",
  "start_round",
  "end_round",
  "save",
  "action",
  "move",
  "trample",
  "damage",
  "aura",
  "heat",
  "track_damage",
];

function resolveSingleTarget(args: SingleTargetArgs): ResolveResult {
  const { state, intent, input, actorId = "authority", effects, sharedDamage, measureFrom, areaSite, ledger } = args;
  // The fan-out's settled target count, or `1` — this spine resolves against exactly one
  // character, so an unthreaded count is genuinely a single target, not an unknown.
  const targetCount = args.targetCount ?? 1;
  const attacker = requireCombatant(state, intent.attacker);
  const target = resolvePrimaryTarget(state, intent.target);

  // destructible-objects Phase 1.3 (`decisions/0057`): objects/deployables aren't
  // characters — RAW gives them no E-DEFENSE and no saves. A tech attack
  // (`intent.tech`, A6 — INVADE and the Quick Tech actions) kind-rejects a
  // non-character target with a clear error rather than rolling against a faked or
  // `undefined` `edef`. Non-tech (weapon/melee) attacks are unaffected — those
  // resolve vs Evasion, which every object legitimately carries.
  //
  // Gates on `isCharacter`, NOT `isActor` (`drones-and-mines` D2, `decisions/0074`):
  // a drone is a non-actor (no turn) but IS a character — Lock On / Scan it, it rolls
  // saves — so it must be a legal tech-attack target. `isActor` would wrongly reject it.
  if (intent.tech && !isCharacter(target)) {
    throw new ResolveError(
      `resolve: tech attacks can't target ${target.kind} "${target.name}" (objects have no E-DEFENSE)`,
    );
  }

  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  // `npc-features-fielded-roster` Phase 13.2 — forced attacks the S9b settled-damage pass queued.
  const forcedAttacks: ForcedAttackRequest[] = [];

  // ── S1: the entity overlay ({@link attackOverlay}) ──
  const overlay = attackOverlay(effects);
  // The attack's own answer source for `choose` atoms and `gm_ruling` predicates: interactive
  // resolutions halt on a missing answer, replays and fixtures read what was recorded.
  const rulings = effects?.interactive ? promptingChoices(intent.choices) : recordedChoices(intent.choices);

  // ── S2: the attack's derived facts ──
  // Extracted to {@link deriveAttackFacts} (`attackFacts.ts`) — its per-field doc comments carry
  // the full account of each fact: which object it is read off (the multi-profile PARENT vs the
  // selected firing profile), why, and which `match` field it feeds. The firing-profile
  // selection is delegated to `firingProfile` rather than restated inline (tracker D5.1).
  //
  // These are ALSO the facts `fireAttackDeclared` reads — one derivation, two callers, which is
  // what makes the two agree by construction instead of by hope (tracker D5.2).
  //
  // The overlay fold that consumes them is the S3 call just below.
  const {
    baseWeaponId,
    isActionAttack,
    weapon,
    rangedAttack,
    techAttack,
    weaponType,
    mount,
    firstAttackThisTurn,
    attackVerb,
    actionId,
    fx,
    reaction,
  } = deriveAttackFacts({ state, attacker, intent, effects });

  // `ll0-dsl-primitives-2` Phase E (`decisions/0085`): every `modifier_consume_resource_on_apply`
  // cap this resolution actually spent — from the attacker/target modifier fold ({@link
  // foldAttackModifiers}, spliced on at the S3 seam just below) AND (later in this function) the
  // on-hit/on-crit trigger fire. Turned into `resource_delta` +1 steps at this function's own
  // return, then spliced onto the room's committed resolution ONLY (`encounterRoom.ts`, mirroring
  // `ordinalIncrement` — see `Resolved.resourceCapConsumeSteps`).
  const consumedResourceCaps: { carrier: string; key: string }[] = [];

  // ── S2.5: the cover fold ──
  // Extracted to {@link foldAttackCover} (`attackModifiers.ts`) — its comments carry the full
  // RAW account: the ranged-only gate, Seeking's exemption, tg_thrown's carve-back, the "Soft
  // Cover" status floor, and the Blast `measureFrom` anchor. It is PRE-DRAW (before any dice) and pure, so replay/undo stay byte-stable —
  // it contributes one more net-accuracy term and nothing else.
  //
  // **It ran at S6 (after the modifier fold, the variable-damage-type prompt and Invisible's
  // miss coin) until `npc-features-fielded-roster` Phase 8.1 moved it here.** The S3 fold just
  // below now threads the settled `degree` onto its predicate context as the attack-family
  // `cover` event fact, so a `modifier` clause can be gated on "the target is not in cover"
  // (Sniper's Mark, Core p.309) — which requires cover to be settled first. The move is
  // observationally inert: this fold is pure, draws no dice, emits no step, and its two
  // outputs are read no earlier than they were, so the only behavioural difference is that
  // the two paths which return before S7 (S4's prompt exit, S5's miss coin) now compute a
  // cover answer they discard. Do not move it back.
  const { degree: coverDegree, acc: coverAcc } = foldAttackCover({
    state,
    attacker,
    target,
    intent,
    weapon,
    isActionAttack,
    ...(measureFrom !== undefined ? { measureFrom } : {}),
    overlay,
  });

  // ── S3: the pre-roll overlay modifier fold ──
  // Extracted to {@link foldAttackModifiers} (`attackModifiers.ts`) — its per-field doc comments
  // carry the full account of each facet: which side folds it, which RAW mechanic it serves, and
  // where in this function it is read. It is a PURE fold: it returns its advisory `warnings` and
  // its `consumedResourceCaps` rather than pushing onto this function's accumulators, and the two
  // are concatenated at this seam, in the same order the inline fold pushed them.
  //
  // Effective net accuracy = intent.accuracy + (attacker's modifiers, e.g. Impaired = −1) +
  // (target's modifiers, e.g. Lock On = +1) + the S2.5 cover term settled just above. Statuses live in the
  // **overlay** now (the hand-rolled `statusEffects` table is retired — Phase 8): Impaired/Lock
  // On/Shredded are overlay `modifier`s with math; Stunned/Slowed/Immobilized/Prone are overlay
  // *advisory* modifiers (no math, only a warning + Prone's deferred spatial note).
  //
  // **The Lock On consume choice is settled INSIDE this stage** ({@link settleConsumeChoice}): a
  // discovery fold finds the consumable statuses the target carries; when there are any, the
  // wielder's answer is read (or asked for, before any dice) and the fold re-runs with it, so a
  // kept Lock On contributes no +1 and a consumed one reaches `match.consumes`. The re-fold only
  // runs against a target carrying a consumable status, so every other attack folds once.
  const foldArgs = {
    state,
    attacker,
    target,
    overlay,
    rulings,
    weapon,
    rangedAttack,
    techAttack,
    weaponType,
    mount,
    firstAttackThisTurn,
    attackVerb,
    actionId,
    cover: coverDegree,
    targetCount,
    chosen: ledger.choices,
    costs: ledger,
  };
  let consumeFold = foldAttackModifiers(foldArgs);
  let consumeChoice: ConsumeChoice = { consume: [], keep: [] };
  if (consumeFold.consumeOnHit.length > 0) {
    const settled = settleConsumeChoice({
      attacker,
      consumable: consumeFold.consumeOnHit,
      choices: intent.choices,
      interactive: effects?.interactive,
    });
    if (isNeedsDecision(settled)) return settled;
    consumeChoice = settled;
    consumeFold = foldAttackModifiers({ ...foldArgs, consumeChoice });
  }
  const {
    attackerAccuracy: overlayAttackerAcc,
    targetAccuracy: overlayTargetAcc,
    accuracyContributions: overlayAccuracyContributions,
    ignoreTargetArmor: overlayIgnoreArmor,
    ignoreCover: overlayIgnoreCover,
    doubleDamage: overlayDoubleDamage,
    substituteStructureDamage: overlaySubstituteStructure,
    evasionCap: overlayEvasionCap,
    missChances: overlayMissChances,
    overrideDamageType: overlayOverrideDamageType,
    attackRoll: overlayAttackRoll,
    armorPiercing: overlayArmorPiercing,
    bonusDamageType: overlayBonusDamageType,
    warnings: modifierWarnings,
    consumedResourceCaps: modifierResourceCaps,
  } = consumeFold;
  warnings.push(...modifierWarnings);
  // Raised as soon as the fold counts them, so every later exit spends them: the attack was made
  // even when Invisible's coin then misses it outright (BC-026).
  for (const { carrier, key } of modifierResourceCaps) emitEvent(ledger, { kind: "resource_cap_use", carrier, key });

  // ── S4: the variable damage-type settlement ──
  // Extracted to {@link resolveVariableDamageType} (`attackPreRoll.ts`) — its comments carry the
  // full A2-finish account: the recorded-choice roundtrip, the one-prompt-per-attack rule a
  // `variable` RIDER inherits, the deterministic non-interactive default, and why an
  // attacker-side `override_attack_damage_type` wins outright over the whole block.
  //
  // The first exit-carrying stage after S3's consume choice: it either settles the two damage-type
  // locals or returns the `needs_decision` prompt verbatim, which the one-line guard below
  // forwards. Its prompt is routed to the ATTACKER (the wielder picks its own weapon's type),
  // constructed inside the stage — there is no `sig.who` fallback at this exit.
  const variableDamageType = resolveVariableDamageType({
    attacker,
    weapon,
    overrideDamageType: overlayOverrideDamageType,
    choices: intent.choices,
    interactive: effects?.interactive,
  });
  if (isNeedsDecision(variableDamageType)) return variableDamageType;
  const { dtype, resolvedVariableType } = variableDamageType;

  // ── S5: the attack attempt, and Invisible's pre-roll coin ──
  // The attempt (`event-engine-to-hit`): the coin, the roll and the outcome are all caused by it. A
  // declared reaction's miss (Interdiction Point) or a forced `autoHit` rides it as `forced`.
  // Invisible's (and Mirage's) miss chance is its replacement: ONE recorded coin, a d2, drawn
  // strictly after S4's prompt exit (so a re-resolve replays it) and strictly before the d20, per
  // target inside an area fan-out. On the miss face the attack misses outright, with no d20, and
  // runs the miss path like any other miss (R2, BC-050).
  const loop = (ledger.loop ??= createEventLoop({ dice: input.dice }));
  // What the reactions to the declaration did to this target (R11): raised into the loop since this resolution began.
  const facts = loop.log.slice(ledger.opened ?? 0).map((h) => h.event);
  const interdictedBy = facts.find((e): e is AttackForcedMissEvent => e.kind === "attack_forced_miss" && e.target === target.id)?.by;
  const forced: AttackForced | undefined = interdictedBy !== undefined ? { verdict: "miss", by: interdictedBy } : effects?.forced?.autoHit === true ? { verdict: "hit" } : undefined;
  const attempt = loop.raise(
    { kind: "attack_attempt", attacker: attacker.id, target: target.id, ...(forced !== undefined ? { forced } : {}) },
    { replacements: missChanceReplacements(overlayMissChances, { loop, dice: input.dice, attacker: attacker.id, target: target.id }), trigger: (h) => h },
  )!;

  // ── S7: the to-hit draw, and the outcome ──
  // {@link drawToHit} (`toHit.ts`) rolls the attack and proposes the outcome. The outcome's
  // replacements (Hunker Down, Barrel Roll, Valiant Aid; `toHitReactionRegistry.ts`) rewrite it
  // before it lands, asking their deciders; its landing grounds the `to_hit` step once, from the
  // final values; and its trigger is the rest of this attack: S8 to S12 in order, then S13, the
  // reactions to it. A halt anywhere in there re-runs the whole resolution from the top against the
  // same recorded dice and choices. An unanswered reaction prompt's FALLBACK recipient is the
  // ATTACKER: an offer that names a third combatant (a turret's deployer, Valiant Aid's Exemplar, a
  // missed defender) supplies `sig.who` itself.
  const draw = drawToHit({
    attacker,
    target,
    intent,
    weapon,
    overlay,
    techAttack,
    fx,
    attackerAccuracy: overlayAttackerAcc,
    targetAccuracy: overlayTargetAcc,
    accuracyContributions: overlayAccuracyContributions,
    evasionCap: overlayEvasionCap,
    attackRoll: overlayAttackRoll,
    // An attacker-side `ignoreCover` (Vanguard 2) drops the cover term the S2.5 fold settled.
    coverAcc: overlayIgnoreCover ? 0 : coverAcc,
    coverDegree: overlayIgnoreCover ? "none" : coverDegree,
    dice: input.dice,
    loop,
    attempt,
  });
  // The facts every to-hit reaction reads, on a given verdict.
  const reactionCtx = (isHit: boolean, isCrit: boolean): ToHitReactionContext => ({
    state,
    attacker,
    target,
    isHit,
    isCrit,
    choices: intent.choices,
    ...interactiveFire(effects),
    dice: input.dice,
    overlay,
    spentReactions: reactorsUsed(ledger),
    // A forced attack with `reactions: false` (Rebound's reflection) opens no reaction.
    ...(effects?.forced?.reactions === false ? { offersClosed: true } : {}),
    tech: techAttack,
    consumes: consumeChoice.consume,
    // A reaction that flies (Barrel Roll, Ace's Acrobatics) reads the recorded direction answer.
    ...(intent.reactionToward !== undefined ? { toward: intent.reactionToward as Cell } : {}),
  });
  const proposedHit = draw.outcome.verdict !== "miss";
  const replacements = outcomeReplacements(reactionCtx(proposedHit, draw.outcome.verdict === "crit"), {
    reroll: draw.reroll,
    accepted: (out) => {
      useReaction(ledger, out.spendReactionBy);
      if (out.forcedAttacks) forcedAttacks.push(...out.forcedAttacks);
    },
    land: (landed) => steps.push(...landed),
  });

  /** S8 to S13: everything the landed outcome causes. Returns the resolution, or a halt. */
  const consequences = (landed: Happened): ResolveResult => {
    const outcome = landed.event as AttackOutcomeEvent;
    const isHit = outcome.verdict !== "miss";
    const isCrit = outcome.verdict === "crit";
    const rawD20 = outcome.d20;
    // Hunker Down's Resistance, or a declared `resist_pending_damage`, reaches the damage window.
    const toHitResistant = outcome.resistant === true || facts.some((e) => e.kind === "attack_resisted" && e.target === target.id);
    if (outcome.missChance !== undefined) {
      warnings.push(`target is Invisible — the pre-roll ${Math.round(outcome.missChance.chance * 100)}% miss chance triggered; the attack missed outright before the d20`);
    } else {
      // The roll's outcome, for a cost replacement at settlement (Efficiency reads the first one).
      (ledger.attackRolls ??= []).push({ target: target.id, hit: isHit, crit: isCrit });
    }

    // ── S8: the weapon-fire bookkeeping ──
    // Extracted to {@link foldWeaponFireBookkeeping} (`weaponConsume.ts`) — its comments carry the
    // full account of each clause: Recharge discharge, the Loading and Limited consume-on-fire
    // steps, and the Heat (Self) amount, all unconditional on hit or miss, all skipped when
    // `sharedDamage` is set because the weapon fired ONCE regardless of how many targets an area
    // template caught.
    //
    // The stage carries no exit. `selfHeatAmt` is returned rather than emitted: on the hit arm it is
    // folded into the SAME single `resolveHeatCascade` call as Overkill's heat, inside
    // {@link applyHitDamage} (`damageApply.ts`); the Reliable-floor arm ({@link
    // applyReliableFloorDamage}) and the plain-miss arm just below in this function each spend it on
    // their own. Splitting the hit arm's fold into two calls would recompute the cap-cross from the
    // same pre-resolution heat
    // twice. `resolveAreaAttack` keeps its own once-per-attack copy of this bookkeeping, which is
    // NOT this function and must not be unified with it (the gating and the cascade differ).
    const { costs: fireCosts, selfHeatAmt } = foldWeaponFireBookkeeping({
      attacker,
      weapon,
      sharedDamage: sharedDamage !== undefined,
      // A forced attack with `consume: false` (Rebound's reflection, Phase 11.1).
      consume: effects?.forced?.consume !== false,
      // A weapon's own secondary attack (the Annihilator's) charges no Heat (Self).
      secondary: effects?.forced?.secondary === true,
    });
    // Settled at the action's end with the other costs (BC-027).
    for (const cost of fireCosts) emitEvent(ledger, cost);
    // A Thrown weapon thrown past its Threat lands downrange, to be retrieved (`thrownLandingSteps`).
    if (sharedDamage === undefined && !intent.tech) {
      const thrown = thrownLandingSteps({ state, attacker, target, weapon });
      steps.push(...thrown.steps);
      warnings.push(...thrown.warnings);
    }

    // 2 + 3. Damage (A2 — typed, weapon-driven, mitigated). A hit/crit rolls the
    // weapon's damage dice, floored at Reliable N; a **miss** still deals Reliable N
    // if the weapon is Reliable. Overkill rerolls 1s and deals heat to the wielder. Burn-
    // type damage ignores armor and is marked for the turn-start tick. (`weapon` is the
    // active firing profile {@link deriveAttackFacts} settled at S2, so its tags feed the
    // modifier fold + on_crit gather.)
    // The effective target armor: 0 if a target-side modifier ignores it (Shredded), or
    // if the weapon is AP (the existing flatten); else the frozen armor. Shredded folds
    // into the `damage` step's mitigation — no step of its own (D3).
    const effectiveArmor = effectiveTargetArmor(target, overlayIgnoreArmor);
    // Exposed (A3): double the physical (kinetic/energy/explosive) pre-mitigation damage.
    const doublePhysical = overlayDoubleDamage && isPhysical(dtype);

    // A6: a **tech attack** (INVADE) deals NO weapon damage — its consequences (2 heat +
    // the FRAGMENT SIGNAL menu) ride the on-hit trigger bus below. So the weapon-damage /
    // Reliable block is skipped entirely for a tech attack; the to-hit record stands and
    // the on-hit effects splice in. (A weapon attack runs the damage block as before.)
    // A **no-damage melee action attack** (Ram/Grapple, project melee-control-actions) is the same
    // shape on the melee/Evasion side: no weapon damage, only its on-hit overlay consequence (Prone
    // + push / the Grappled bind). Scoped to {@link isNoDamageMeleeAction} (Ram/Grapple by id) — NOT
    // the profile's `damage.count === 0` (that WAS an equivalent proxy pre-`npc-turn-control`, but an
    // NPC attacker's Improvised Attack profile is now ALSO `count:0`, its flat RAW tier damage, so the
    // proxy would wrongly swallow it too) — because **Improvised Attack** (`raw-fidelity-sweep` item 2,
    // RAW Core p.71) is ALSO a melee action attack (adjacency-gated, vs Evasion) but deals REAL weapon
    // damage (rolled 1d6 for a PC, flat tier damage for an NPC) and must fall through to the ordinary
    // damage path below like any other hit.
    const archaic = !intent.tech && archaicCantHarm(weapon, target);
    if (archaic) warnings.push(`${weapon.name} is Archaic and can't harm mechs — ${target.name} takes no damage.`);
    const skipWeaponDamage = intent.tech || isNoDamageMeleeAction(intent.weapon) || archaic;
    // An attacker-side AP grant (Walking Armory's SABOT) reaches mitigation as the firing profile's AP.
    const damageWeapon = overlayArmorPiercing && !weapon.ap ? { ...weapon, ap: true } : weapon;
    // The Reliable-floor stage, shared by a miss (S9c) and the Unraveler's weak hit (S9b).
    const reliableFloor = (carriedReaction?: HitDamageApplication["reaction"]) =>
      applyReliableFloorDamage({
        ...(carriedReaction !== undefined ? { carriedReaction } : {}),
        state,
        attacker,
        target,
        intent,
        weapon: damageWeapon,
        overlay,
        effects,
        dice: input.dice,
        landedOn: loop.scratch(state),
        dtype,
        doublePhysical,
        ignoreTargetArmor: overlayIgnoreArmor,
        effectiveArmor,
        selfHeatAmt,
        rangedAttack,
        techAttack,
        spentReactions: reactorsUsed(ledger),
        loop,
      });
    // How many times heat the on-hit clauses add to the target is halved (`HitDamageApplication.targetHeatHalvings`).
    let heatHalvings: number | undefined;
    // Whether the target's accepted reaction resists this attack (`HitDamageApplication.targetReactionResisted`).
    let reactionResisted = false;
    // The Armor the hit's own damage left for its on-hit riders (`armorLeftAfterHit`): all of it
    // when no weapon damage was rolled (a Ram, a tech attack).
    let armorLeft = effectiveArmor;
    if (skipWeaponDamage) {
      // no weapon-damage roll for a tech / no-damage melee-action attack
    } else if (isHit) {
      // ── S9b: the hit-damage application ──
      // Extracted to {@link applyHitDamage} (`damageApply.ts`) — its comments carry the full
      // account of every sub-block: the up-front per-instance draw (primary then riders, ALL dice
      // before anything is mitigated — the replay-prefix pin), the damage-interception reaction
      // window and the both-ways `incomingDamage` preview it dresses its halt with, the
      // protector split/redirect link, the sequential per-instance mitigation + type routing
      // onto the state this intent's earlier steps left, and the combined Overkill + Heat (Self)
      // cascade onto the wielder.
      //
      // The chain preamble above (`effectiveArmor`, `doublePhysical`, `skipWeaponDamage`) stays
      // here because the Reliable-floor arm below threads it into its own stage too.
      //
      // The stage carries TWO exits, both defaulting `who` to the **TARGET** when the caught
      // signal names nobody — the opposite actor from S7's two, because both prompts here are the
      // defender's to answer (whether to Brace; which system a System Trauma cascade destroys).
      // Its `steps`, `warnings` and reactor entries are RETURNED rather than pushed, and spliced
      // onto this function's accumulators here in the order the inline pushes produced them.
      // The Unraveler (`weaponOwnDamage.ts`): a hit that neither destroys the target nor deals it
      // structure damage deals only the Reliable floor. The hit folds into nothing until it stands,
      // so a discarded hit leaves the scratch untouched.
      const unravels = weapon.reliable > 0 && reliableUnlessStructure(overlay, baseWeaponId);
      const hitDamage = applyHitDamage({
        state,
        attacker,
        target,
        intent,
        // The Fusion Rifle: the hit's damage is the distance to this target (`weaponOwnDamage.ts`).
        weapon: distanceScaledWeapon({ state, attacker, target, weapon: damageWeapon, weaponId: baseWeaponId, overlay }),
        overlay,
        effects,
        dice: input.dice,
        landedOn: loop.scratch(state),
        sharedDamage,
        isCrit,
        dtype,
        resolvedVariableType,
        overrideDamageType: overlayOverrideDamageType,
        doubleDamage: overlayDoubleDamage,
        substituteStructureDamage: overlaySubstituteStructure,
        ignoreTargetArmor: overlayIgnoreArmor,
        effectiveArmor,
        selfHeatAmt,
        rangedAttack,
        techAttack,
        toHitResistant,
        spentReactions: reactorsUsed(ledger),
        events: {
          loop,
          roller: attacker.id,
          cause: { event: landed.id },
          // Brutal's maximize matches the outcome's natural 20 and crit: the roll that stands.
          replacements: damageRollReplacements({ ...foldArgs, consumes: consumeChoice.consume, outcome: { hit: isHit, crit: isCrit, natural20: rawD20 === 20 } }),
        },
      });
      if (isNeedsDecision(hitDamage)) return hitDamage;
      heatHalvings = hitDamage.targetHeatHalvings;
      reactionResisted = hitDamage.targetReactionResisted === true;
      const applied = unravels && !hitReachesStructure(hitDamage.steps, target.id) ? reliableFloor(hitDamage.reaction) : hitDamage;
      if (isNeedsDecision(applied)) return applied;
      armorLeft = armorLeftAfterHit(applied.steps, target.id, effectiveArmor);
      steps.push(...applied.steps);
      warnings.push(...applied.warnings);
      useReaction(ledger, applied.spendReactionBy);
      if (applied.forcedAttacks) forcedAttacks.push(...applied.forcedAttacks);
      if (applied.consumedCaps) consumedResourceCaps.push(...applied.consumedCaps);
    } else if (weapon.reliable > 0) {
      // ── S9c: the Reliable-floor miss ──
      // Extracted to {@link applyReliableFloorDamage} (`damageApply.ts`, alongside the hit arm) —
      // its comments carry the full account: the flat floor (doubled by Exposed), the miss-side
      // damage window (authored offers only — Rebound, never Brace), the mitigate + immunity + type
      // routing, and the Heat (Self) cost a miss still owes because firing is what charges it.
      //
      // Its ONE exit — the System Trauma prompt the floor's own cascade can raise — defaults `who`
      // to the **TARGET**, like both of the hit arm's, since the prompt asks the defender which of
      // its systems it loses.
      const reliableDamage = reliableFloor();
      if (isNeedsDecision(reliableDamage)) return reliableDamage;
      steps.push(...reliableDamage.steps);
      warnings.push(...reliableDamage.warnings);
      if (reliableDamage.forcedAttacks) forcedAttacks.push(...reliableDamage.forcedAttacks);
      if (reliableDamage.consumedCaps) consumedResourceCaps.push(...reliableDamage.consumedCaps);
      // An accepted damage-window offer (Rebound) spends a reaction.
      useReaction(ledger, reliableDamage.spendReactionBy);
    } else if (selfHeatAmt > 0) {
      // A plain miss (no Reliable floor) on a `tg_heat_self` weapon: no damage step at
      // all, but the weapon still FIRED — the self-heat cost is unconditional on hit/miss.
      const heatCascade = resolveHeatCascade({ target: attacker, amount: selfHeatAmt, dice: input.dice, turnOrdinal: state.activation.turnOrdinal, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, attackOverlay(effects), { loop }) });
      steps.push(...heatCascade.steps);
      warnings.push(...heatCascade.warnings);
    }

    // ── S10: clear the consumed statuses ──
    // The wielder chose at S3 ({@link settleConsumeChoice}); RAW clears a consumed Lock On "after
    // that attack resolves", so it clears on a hit and on a miss alike ({@link consumeStatusCosts}).
    //
    // **Inside an area fan-out the choice is asked ONCE for the whole attack**: its prompt id is
    // `consume:<status>`, keyed on the status and NOT on the target, so the first caught target's
    // halt collects the wielder's answer and every later caught target reads the same recorded
    // answer. A future MULTI-TARGET `choose` atom must put the target id in its prompt id, or one
    // answer will silently apply to every target.
    steps.push(...spendStepsNow(consumeStatusCosts(target, consumeChoice.consume), state, loop));

    // `area-bonus-damage-halving` Phase 1: **is this resolution one target of an attack that
    // targets more than one character?** Derived from `sharedDamage !== undefined` — the area
    // fan-out's own marker, and the SAME derivation this function already hands
    // {@link import("./weaponConsume").foldWeaponFireBookkeeping} under the same meaning (see its
    // call above). Threaded into both trigger fires below so an `extra_damage` rider flagged
    // `bonusDamage: true` halves per RAW (Core p.67's sidebar, restated p.78).
    //
    // Deliberately NOT re-derived by re-reading the weapon's range: `isAreaRange` would have to be
    // re-called against a range this function doesn't hold, and — the real reason — the range says what the WEAPON is
    // while `sharedDamage` says what this RESOLUTION actually did. Two shapes diverge, and in both
    // the resolution genuinely targets one character, so both correctly read `false`: a REACTION
    // (`intent.reaction` forces the single-target spine at {@link resolve}'s dispatch — Overwatch
    // never fans out) and an UNANCHORABLE template (an unplaced attacker or unplaced unit target
    // degrades `resolveAreaAttack` to this function). Halving either would be a pure under-grant.
    // `decisions/0051` D2 said "keyed off the weapon profile's range type"; these two divergences
    // from its letter are recorded in this project's tracker rather than silently chosen.
    const areaAttack = sharedDamage !== undefined;

    // ── S11: the on_hit/on_crit trigger fire ──
    // Extracted to {@link import("./attackTriggerFires").fireHitTriggers} — its comments carry the
    // full account: the base-weapon-vs-action-id gather, the attacker's weapon + pilot + status +
    // system handler merge, the `optional: true` filter, the whole FireContext field by field, and
    // the on_hit/on_crit DISSOLVE (0112 §2) that made this ONE fire against the `"attack"` family.
    //
    // The stage carries ONE exit — an unanswered on-hit `choose` in interactive mode — defaulting
    // its recipient to the ATTACKER when the caught signal names nobody, the opposite actor from
    // the damage stages' three. Its `steps`, `warnings` and `consumedResourceCaps` entries are
    // RETURNED rather than pushed, and spliced onto this function's accumulators here in the order
    // the inline pushes produced them.
    //
    // **It is deliberately a SEPARATE function from the on_miss fire below, and they must never be
    // unified** — that constraint moved with the code and now lives in `attackTriggerFires.ts`'s
    // module doc, which is where a session tempted to collapse them will be reading.
    // The hit lands in the loop's scratch before its listeners read it.
    ledger.loop?.land(state, steps);
    const hitTriggerFire = fireHitTriggers({
      state,
      attacker,
      target,
      intent,
      weapon,
      baseWeaponId,
      overlay,
      areaSite,
      areaAttack,
      ledger,
      dice: input.dice,
      effects,
      isHit,
      isCrit,
      rawD20,
      rangedAttack,
      techAttack,
      weaponType,
      mount,
      firstAttackThisTurn,
      attackVerb,
      cover: coverDegree,
      targetCount,
      consumes: consumeChoice.consume,
      reaction,
      bonusDamageType: overlayBonusDamageType,
      ...(heatHalvings !== undefined ? { heatHalvings } : {}),
      ...(reactionResisted ? { reactionResisted } : {}),
      armorLeft,
    });
    if (isNeedsDecision(hitTriggerFire)) return hitTriggerFire;
    steps.push(...hitTriggerFire.steps);
    warnings.push(...hitTriggerFire.warnings);
    consumedResourceCaps.push(...hitTriggerFire.consumedResourceCaps);
    forcedAttacks.push(...hitTriggerFire.forcedAttacks);

    // ── S12: the on_miss trigger fire ──
    // Extracted to {@link import("./attackTriggerFires").fireMissTriggers} — its comments carry the
    // full account: why `seedHandlers` cannot be reused here, the hand-composed attacker+target
    // interleave and why two `gatherHandlersAt` calls would reorder it, the defender-side widening
    // (t_ace:1's Acrobatics), and the `optional: true` filter Valiant Aid depends on.
    //
    // Same single exit shape as the hit fire above, also defaulting its recipient to the ATTACKER,
    // and the same return-and-splice discipline for all three accumulators.
    ledger.loop?.land(state, steps);
    const missTriggerFire = fireMissTriggers({
      state,
      attacker,
      target,
      intent,
      weapon,
      baseWeaponId,
      overlay,
      areaSite,
      areaAttack,
      ledger,
      dice: input.dice,
      effects,
      isHit,
      rawD20,
      rangedAttack,
      techAttack,
      weaponType,
      mount,
      firstAttackThisTurn,
      attackVerb,
      cover: coverDegree,
      targetCount,
      consumes: consumeChoice.consume,
      reaction,
    });
    if (isNeedsDecision(missTriggerFire)) return missTriggerFire;
    steps.push(...missTriggerFire.steps);
    warnings.push(...missTriggerFire.warnings);
    consumedResourceCaps.push(...missTriggerFire.consumedResourceCaps);

    // ── S13: the reactions to the outcome (`event-engine-to-hit` R1, R4, R5) ──
    // Ace's Acrobatics, Turret Attack and the bystanders react to the landed outcome, after the
    // attack's own consequences ("reactions resolve after the action that triggers them", Core p.61),
    // on its final verdict and the state the attack left, in the F2a order. A reactor who already
    // spent its reaction on this attack (a protector's Intercede) is not offered another.
    ledger.loop?.land(state, steps);
    for (const out of outcomeReactions({ ...reactionCtx(isHit, isCrit), state: loop.scratch(state) })) {
      // Each reaction gets its own link, handing back to the attacker after it (4.3).
      steps.push(...reactionLinked(out, attacker.id));
      useReaction(ledger, out.spendReactionBy);
      if (out.forcedAttacks) forcedAttacks.push(...out.forcedAttacks);
    }

    const command: ApplyResolvedCommand = {
      type: "apply_resolved",
      actorId,
      steps,
      kind: "attack",
    };

    // `ll0-dsl-primitives-2` Phase E (`decisions/0085`): every cap the trigger fires spent becomes a
    // `resource_delta` +1 step on `Resolved.resourceCapConsumeSteps` (see its own doc for why not
    // `steps`). It is raised here as an event and `resolve()` settles it (`decisions/0211`).
    for (const { carrier, key } of consumedResourceCaps) emitEvent(ledger, { kind: "resource_cap_use", carrier, key });

    return {
      kind: "resolved",
      command,
      steps,
      warnings,
      ...(forcedAttacks.length > 0 ? { forcedAttacks } : {}),
      ...(hitTriggerFire.movePath !== undefined ? { movePath: hitTriggerFire.movePath } : {}),
    };
  };

  try {
    return loop.raise(draw.outcome, { replacements, happen: (event) => steps.push(draw.ground(event as AttackOutcomeEvent)), trigger: consequences }, { event: attempt.id })!;
  } catch (sig) {
    if (!(sig instanceof NeedsDecisionSignal)) throw sig;
    return {
      kind: "needs_decision",
      who: sig.who ?? attacker.id,
      promptId: sig.promptId,
      prompt: sig.prompt,
      options: sig.options,
      answerKind: sig.answerKind,
      ...(sig.title !== undefined ? { title: sig.title } : {}),
    };
  }
}
